import React, { useState } from 'react';
import 'twin.macro';
import _last from 'lodash-es/last';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { useGetDaysWithEvents } from '@teetsh/app/src/containers/CahierJournal/queries/useGetDaysWithEvents';
import { SchoolyearSelector } from '@teetsh/app/src/containers/CahierJournal/CopyEventsToAnotherDayModal/SchooyearSelector';
import { SchoolSelector } from '@teetsh/app/src/containers/CahierJournal/CopyEventsToAnotherDayModal/SchoolSelector';
import { Schoolyear } from '@teetsh/app/src/types/schoolyear';
import { EventsPreview } from '@teetsh/app/src/containers/CahierJournal/CopyEventsToAnotherDayModal/EventsPreview';
import { School } from '@teetsh/app/src/types/school';
import {
  useResetSelectedPreviewSeances,
  useSingleSelectedSeance,
} from '@teetsh/app/src/containers/CahierJournal/CopyEventsToAnotherDayModal/atom';
import { SchoolDatePicker } from '@teetsh/app/src/components/Form/DatePicker/SchoolDatePicker';
import { useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { computeDatePickerSelectedDate } from '@teetsh/app/src/containers/CahierJournal/CopyEventsToAnotherDayModal/computeDatePickerSelectedDate';
import { getNearestDate } from '@teetsh/app/src/utils/time';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { Hint } from '@teetsh/app/src/components/Hint';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import { format } from '@teetsh/app/src/utils/time/format';
import useSchoolyearsWithSchoolsAndClassrooms from '@teetsh/app/src/queries/schoolyears/useSchoolyearsWithSchoolsAndClassrooms';
import { UserLocale } from '@teetsh/app/src/types/user';

export interface Props {
  withPreviewEvents?: boolean;
  onChangeDate: (v: Date) => void;
  classrooms?: Classroom[];
  date: Date;
  oppositeDate: Date;
  editable?: boolean;
  schoolyear: Schoolyear;
  school?: School;
  onChangeSchoolyear: (v?: Schoolyear) => void;
  onChangeSchool: (v?: School) => void;
  source?: boolean;
}

export function CopyEventsToAnotherDay(props: Props) {
  const {
    withPreviewEvents,
    classrooms = [],
    school,
    schoolyear,
    onChangeSchoolyear,
    onChangeSchool,
    onChangeDate,
    source,
    date,
    oppositeDate,
  } = props;

  const { data: hydratedSchoolyears = [] } =
    useSchoolyearsWithSchoolsAndClassrooms();
  const resetSelectedSeances = useResetSelectedPreviewSeances();
  const { data: daysWithEvents = [] } = useGetDaysWithEvents({
    schoolId: school?.id,
    schoolyearId: schoolyear?.id,
  });

  const { guestMode } = useUser();
  const { t } = useTranslation(['common', 'cahierJournal']);
  const singleSelectedSeance = useSingleSelectedSeance();
  const [selectedMonth, setSelectedMonth] = useState<Date>(date);

  const handleSchoolyearChange = (v: Schoolyear) => {
    if (source) {
      resetSelectedSeances();
    }
    const schoolyearSchools =
      hydratedSchoolyears.find(sy => sy.id === v.id)?.schools || [];
    onChangeSchoolyear(v);
    onChangeSchool(
      schoolyearSchools.length > 0 ? schoolyearSchools[0] : undefined,
    );

    const selectedDate = computeDatePickerSelectedDate({
      oppositeDate,
      singleSelectedSeance,
      schoolyear: v,
    });
    onChangeDate(selectedDate);
    setSelectedMonth(selectedDate);
  };

  const hasActiveDayThisMonth = daysWithEvents.some(
    d =>
      d.getMonth() === selectedMonth.getMonth() &&
      d.getFullYear() === selectedMonth.getFullYear(),
  );
  const nearestEvent = getNearestDate(daysWithEvents || [], selectedMonth);
  const showNearestEventHint = source && !hasActiveDayThisMonth && nearestEvent;

  return (
    <>
      <div tw="flex gap-x-4">
        {!guestMode?.enabled && (
          <SchoolyearSelector
            value={schoolyear}
            disabled={!props.editable}
            onChange={handleSchoolyearChange}
          />
        )}
        <div tw="flex-grow mb-8">
          {!guestMode?.enabled && (
            <SchoolSelector
              disabled={!props.editable}
              schoolyearId={schoolyear?.id}
              onChange={v => {
                if (source) {
                  resetSelectedSeances();
                }
                if (onChangeSchool) {
                  onChangeSchool(v);
                }
              }}
              value={school}
            />
          )}
        </div>
      </div>
      {daysWithEvents && daysWithEvents.length === 0 && source ? (
        <div tw="text-lg text-center">
          {t('cahierJournal:copyEventsToAnotherDayModal.noSeanceOnThatSchool', {
            schoolName: school?.name || '',
            schoolyear: (schoolyear?.year || '').toString(),
          })}
        </div>
      ) : (
        <div>
          <div data-testid="calendar" tw="flex justify-center mb-8">
            <SchoolDatePicker
              mode="single"
              userLocale={t('common:locale.long') as UserLocale}
              withTime={!!singleSelectedSeance && !source}
              classrooms={classrooms}
              dots={daysWithEvents}
              fromDate={source ? daysWithEvents[0] : undefined}
              toDate={source ? _last(daysWithEvents) : undefined}
              onChange={v => {
                if (v) {
                  onChangeDate(v);
                }
              }}
              onMonthChange={v => setSelectedMonth(v)}
              value={date}
              sundayStart={false}
            />
          </div>
          {withPreviewEvents && date && (
            <div tw="flex-grow">
              <EventsPreview
                date={date}
                schoolyearId={schoolyear?.id}
                schoolId={school?.id}
                classroomsOrg={school?.classroomsOrg || 'unified'}
              />
            </div>
          )}
          {showNearestEventHint && (
            <div tw="mx-auto mt-4 pb-4 px-3">
              <Hint
                icon={
                  <LightBulbIcon tw="h-6 w-6 inline-block mr-1 self-start" />
                }
              >
                {t('cahierJournal:nearestEventHint')}
                <Button
                  tw="mx-auto"
                  minimal
                  small
                  onClick={() => {
                    onChangeDate(nearestEvent);
                    setSelectedMonth(nearestEvent);
                  }}
                >
                  {t('cahierJournal:nearestEventHintLink', {
                    date: format(
                      nearestEvent,
                      t('common:locale.long') as UserLocale,
                      t('common:datePattern.d_MMM_yyyy'),
                    ),
                  })}
                </Button>
              </Hint>
            </div>
          )}
        </div>
      )}
    </>
  );
}
